/**
 * Carousel Preview - Development Test Page
 *
 * This page provides a test harness for the change review carousel component.
 * It allows developers to preview the carousel with mock data without needing
 * full AG-UI backend integration.
 *
 * Access: /dev/carousel-preview (only in development mode)
 *
 * Features:
 * - Mock data generation for pain entries, medications, and events
 * - Toggle between different data scenarios
 * - Console logging of all callback actions
 * - Responsive preview at different sizes
 *
 * @see specs/17_ag_ui_evaluation.md
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useCallback } from "react";
import { ArrowLeft, RefreshCw, Monitor, Smartphone, Tablet } from "lucide-react";
import { formatTodayYmd } from "@/lib/date-format";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ChangeReviewCarousel,
} from "@/components/copilot/change-review";
import type { ExtractedChange, ExtractedPainEntry, ExtractedMedication, ExtractedEvent } from "@/types/check-in-agent";

// ============================================================================
// Mock Data Factories
// ============================================================================

const createMockPainEntry = (id: number, overrides?: Partial<ExtractedPainEntry>): ExtractedChange => ({
  type: "pain_entry",
  data: {
    id: `pain-${id}`,
    region: "lower_back",
    laterality: "right",
    intensity: 7,
    qualities: ["aching", "sharp"],
    notes: "Gets worse when sitting for long periods",
    confidence: 0.92,
    status: "pending",
    ...overrides,
  },
});

const createMockMedication = (id: number, overrides?: Partial<ExtractedMedication>): ExtractedChange => ({
  type: "medication",
  data: {
    id: `med-${id}`,
    name: "Ibuprofen",
    doseValue: 400,
    doseUnit: "mg",
    timeOfDay: "morning",
    notes: "Taken with breakfast",
    confidence: 0.95,
    status: "pending",
    ...overrides,
  },
});

const createMockEvent = (id: number, overrides?: Partial<ExtractedEvent>): ExtractedChange => ({
  type: "event",
  data: {
    id: `event-${id}`,
    eventType: "flare",
    title: "Pain flare-up",
    date: formatTodayYmd(),
    notes: "Started after lifting heavy boxes",
    confidence: 0.88,
    status: "pending",
    ...overrides,
  },
});

// Pre-defined test scenarios
const MOCK_SCENARIOS: Record<string, { name: string; changes: ExtractedChange[] }> = {
  single_pain: {
    name: "Single Pain Entry",
    changes: [createMockPainEntry(1)],
  },
  single_medication: {
    name: "Single Medication",
    changes: [createMockMedication(1)],
  },
  single_event: {
    name: "Single Event",
    changes: [createMockEvent(1)],
  },
  mixed_full: {
    name: "Mixed (Full Check-in)",
    changes: [
      createMockPainEntry(1),
      createMockPainEntry(2, {
        region: "head_neck",
        laterality: "left",
        intensity: 5,
        qualities: ["tight", "throbbing"],
        confidence: 0.78,
      }),
      createMockMedication(1),
      createMockEvent(1),
    ],
  },
  low_confidence: {
    name: "Low Confidence Items",
    changes: [
      createMockPainEntry(1, { confidence: 0.45 }),
      createMockMedication(1, { confidence: 0.55, name: "Acetaminophen?" }),
      createMockEvent(1, { confidence: 0.60, title: "Possible stress trigger" }),
    ],
  },
  many_items: {
    name: "Many Items (Stress Test)",
    changes: [
      createMockPainEntry(1),
      createMockPainEntry(2, { region: "head_neck", intensity: 3 }),
      createMockPainEntry(3, { region: "shoulder", laterality: "left", intensity: 8 }),
      createMockMedication(1),
      createMockMedication(2, { name: "Naproxen", doseValue: 220 }),
      createMockEvent(1),
      createMockEvent(2, { eventType: "medication_start", title: "Started new medication" }),
    ],
  },
};

// ============================================================================
// Component
// ============================================================================

function CarouselPreviewPage() {
  const [selectedScenario, setSelectedScenario] = useState<string>("mixed_full");
  const [changes, setChanges] = useState<ExtractedChange[]>(MOCK_SCENARIOS.mixed_full.changes);
  const [actionLog, setActionLog] = useState<string[]>([]);
  const [previewWidth, setPreviewWidth] = useState<"full" | "tablet" | "mobile">("full");

  const logAction = useCallback((action: string) => {
    setActionLog(prev => [
      `${new Date().toLocaleTimeString()}: ${action}`,
      ...prev.slice(0, 19), // Keep last 20 actions
    ]);
    console.log(`[Carousel Action] ${action}`);
  }, []);

  const handleScenarioChange = (scenarioKey: string) => {
    setSelectedScenario(scenarioKey);
    setChanges(MOCK_SCENARIOS[scenarioKey].changes);
    setActionLog([]);
    logAction(`Loaded scenario: ${MOCK_SCENARIOS[scenarioKey].name}`);
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSaveChange = useCallback(async (changeId: string, edits?: Partial<ExtractedChange["data"]>) => {
    logAction(`onSaveChange called: ${changeId}${edits ? ` with edits: ${JSON.stringify(edits)}` : ''}`);
    setIsSaving(true);
    // Simulate async save
    await new Promise(resolve => setTimeout(resolve, 500));
    setChanges(prev => prev.filter(c => c.data.id !== changeId));
    setIsSaving(false);
  }, [logAction]);

  const handleRejectChange = useCallback(async (changeId: string) => {
    logAction(`onRejectChange called: ${changeId}`);
    setChanges(prev => prev.filter(c => c.data.id !== changeId));
  }, [logAction]);

  const handleSaveAll = useCallback(async () => {
    logAction(`onSaveAll called with ${changes.length} changes`);
    setIsSaving(true);
    // Simulate async save
    await new Promise(resolve => setTimeout(resolve, 1000));
    setChanges([]);
    setIsSaving(false);
  }, [logAction, changes.length]);

  const resetScenario = () => {
    setChanges(MOCK_SCENARIOS[selectedScenario].changes);
    setActionLog([]);
    logAction("Reset to initial state");
  };

  const getPreviewContainerClass = () => {
    switch (previewWidth) {
      case "mobile":
        return "max-w-[375px]";
      case "tablet":
        return "max-w-[768px]";
      default:
        return "max-w-full";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to Dashboard</span>
            </Link>
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">Carousel Preview</h1>
            <p className="text-xs text-muted-foreground">Development Test Page</p>
          </div>
          <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">
            Dev Only
          </Badge>
        </div>
      </header>

      <section className="container py-6 space-y-6">
        {/* Controls Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Test Scenarios</CardTitle>
            <CardDescription>Select a scenario to preview the carousel with different mock data</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Scenario Selection */}
            <div className="flex flex-wrap gap-2">
              {Object.entries(MOCK_SCENARIOS).map(([key, scenario]) => (
                <Button
                  key={key}
                  variant={selectedScenario === key ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleScenarioChange(key)}
                >
                  {scenario.name}
                </Button>
              ))}
            </div>

            <hr className="border-border" />

            {/* Preview Size & Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Preview:</span>
                <div className="flex gap-1">
                  <Button
                    variant={previewWidth === "mobile" ? "secondary" : "ghost"}
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setPreviewWidth("mobile")}
                    title="Mobile (375px)"
                  >
                    <Smartphone className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={previewWidth === "tablet" ? "secondary" : "ghost"}
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setPreviewWidth("tablet")}
                    title="Tablet (768px)"
                  >
                    <Tablet className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={previewWidth === "full" ? "secondary" : "ghost"}
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setPreviewWidth("full")}
                    title="Full Width"
                  >
                    <Monitor className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={resetScenario} className="gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" />
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Carousel Preview */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Carousel Component</CardTitle>
                <CardDescription>
                  {changes.length > 0
                    ? `Showing ${changes.length} change(s) from "${MOCK_SCENARIOS[selectedScenario].name}" scenario`
                    : "All changes have been processed. Click Reset to start over."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className={`mx-auto transition-all duration-300 ${getPreviewContainerClass()}`}>
                  {changes.length > 0 ? (
                    <ChangeReviewCarousel
                      changes={changes}
                      onSaveChange={handleSaveChange}
                      onRejectChange={handleRejectChange}
                      onSaveAll={handleSaveAll}
                      isSaving={isSaving}
                    />
                  ) : (
                    <Card className="p-8 text-center text-muted-foreground border-dashed">
                      <p>No changes to display.</p>
                      <p className="text-sm mt-2">Select a scenario or click Reset.</p>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Action Log */}
          <div className="lg:col-span-1">
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Action Log</CardTitle>
                <CardDescription>Callback events from the carousel</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80 overflow-y-auto rounded-md border bg-muted/30 p-3 font-mono text-xs">
                  {actionLog.length > 0 ? (
                    actionLog.map((log) => (
                      <div
                        key={log}
                        className={`py-1 border-b border-border/50 last:border-0 ${
                          actionLog[0] === log ? "text-foreground" : "text-muted-foreground"
                        }`}
                      >
                        {log}
                      </div>
                    ))
                  ) : (
                    <div className="text-muted-foreground italic">
                      Actions will appear here...
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Current State Display */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Current Changes State</CardTitle>
            <CardDescription>Raw JSON representation of current changes array</CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="overflow-auto rounded-md border bg-muted/30 p-4 text-xs max-h-60">
              {JSON.stringify(changes, null, 2)}
            </pre>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

// ============================================================================
// Route Definition
// ============================================================================

export const Route = createFileRoute("/_authenticated/dev/carousel-preview")({
  component: CarouselPreviewPage,
});
